import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
} from '@/components/ui/select';
import { toaster } from '@/components/ui/toaster';
import { EMAIL_COMPOSER_ROW_LABEL_WIDTH } from '@/components/email-composer/email-composer-layout';
import { useListProgram } from '@/hooks/queries/use-list-program';
import useAffiliateBulkEmailStore, {
  getIncompatibleRecipients,
} from '@/stores/affiliate-bulk-email-store';
import {
  Box,
  Button,
  createListCollection,
  Flex,
  HStack,
  Icon,
  Listbox,
  Popover,
  Text,
} from '@chakra-ui/react';
import { Lock, NavArrowDown, WarningTriangle } from 'iconoir-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface EmailProgramSelectorProps {
  locked?: boolean;
  variant?: 'card' | 'row';
}

function EmailProgramSelector({ locked, variant = 'card' }: EmailProgramSelectorProps) {
  const { t } = useTranslation();
  const programId = useAffiliateBulkEmailStore((s) => s.programId);
  const setProgramId = useAffiliateBulkEmailStore((s) => s.setProgramId);
  const recipients = useAffiliateBulkEmailStore((s) => s.recipients);
  const removeRecipients = useAffiliateBulkEmailStore((s) => s.removeRecipients);
  const { data: programs } = useListProgram();

  const totalRecipients = recipients.size;

  const programRecipientCounts = useMemo(() => {
    if (!programs || totalRecipients === 0) return new Map<string, number>();
    const counts = new Map<string, number>();
    for (const r of recipients.values()) {
      for (const p of r.programs) {
        counts.set(p.id, (counts.get(p.id) ?? 0) + 1);
      }
    }
    return counts;
  }, [programs, recipients, totalRecipients]);

  const incompatible = useMemo(
    () => getIncompatibleRecipients(recipients, programId),
    [recipients, programId]
  );

  const programName = useMemo(() => {
    if (!programId || !programs) return null;
    return programs.find((p) => p.id === programId)?.name ?? null;
  }, [programId, programs]);

  const programCollection = useMemo(() => {
    const items = (programs ?? []).map((p) => {
      const count = programRecipientCounts.get(p.id) ?? 0;
      const disabled = totalRecipients > 0 && count === 0;
      const label =
        totalRecipients > 0
          ? `${p.name} (${t('bulkEmail.compose.program.recipientCount', { count, total: totalRecipients })})`
          : p.name;
      return { value: p.id, label, disabled };
    });
    return createListCollection({
      items,
      isItemDisabled: (item) => Boolean(item.disabled),
    });
  }, [programs, programRecipientCounts, totalRecipients, t]);

  const isLocked = locked && !!programId;
  const isRow = variant === 'row';

  const handleProgramChange = (value: string) => {
    const id = value || null;
    if (id && totalRecipients > 0 && (programRecipientCounts.get(id) ?? 0) === 0) {
      toaster.create({
        title: t('bulkEmail.compose.program.noRecipientsInProgram'),
        type: 'warning',
      });
      return;
    }
    setProgramId(id);
  };

  const handleRemoveIncompatible = () => {
    removeRecipients(incompatible.map((r) => r.id));
  };

  const rowProgramCollection = useMemo(() => {
    const optionalItem = {
      value: '',
      label: t('bulkEmail.compose.program.placeholder'),
      disabled: false,
    };
    return createListCollection({
      items: [optionalItem, ...programCollection.items],
      isItemDisabled: (item) => Boolean(item.disabled),
    });
  }, [programCollection.items, t]);

  const rowTrigger = (
    <Flex align="center" gap={2} minH="28px" w="full">
      <Text fontSize="sm" color="fg.muted" w={EMAIL_COMPOSER_ROW_LABEL_WIDTH} flexShrink={0}>
        {t('bulkEmail.compose.program.label')}:
      </Text>
      <Text
        fontSize="sm"
        color={programName ? 'fg' : 'fg.subtle'}
        fontWeight={programName ? 'medium' : 'normal'}
        truncate
        flex={1}
        minW={0}
      >
        {programName ?? t('bulkEmail.compose.program.placeholder')}
      </Text>
      <Icon color="fg.muted" asChild boxSize={4} flexShrink={0}>
        <NavArrowDown />
      </Icon>
    </Flex>
  );

  return (
    <>
      {isRow ? (
        isLocked ? (
          <HStack minH="28px" gap={3}>
            <Text fontSize="sm" color="fg.muted" w={EMAIL_COMPOSER_ROW_LABEL_WIDTH} flexShrink={0}>
              {t('bulkEmail.compose.program.label')}:
            </Text>
            <Text fontSize="sm" fontWeight="medium" flex={1} truncate>
              {programName ?? programId}
            </Text>
            <Icon color="fg.muted" asChild boxSize={4.5}>
              <Lock />
            </Icon>
          </HStack>
        ) : (
          <Popover.Root positioning={{ placement: 'bottom-start' }}>
            <Popover.Trigger asChild>
              <Box cursor="pointer" rounded="md" _hover={{ color: 'fg' }} transition="color 0.15s">
                {rowTrigger}
              </Box>
            </Popover.Trigger>

            <Popover.Positioner>
              <Popover.Content w="360px" p={0} rounded="lg" shadow="lg" overflow="hidden">
                <Listbox.Root
                  collection={rowProgramCollection}
                  selectionMode="single"
                  value={[programId ?? '']}
                  onValueChange={(e) => handleProgramChange(e.value[0] ?? '')}
                >
                  <Listbox.Content
                    maxH="260px"
                    overflowY="auto"
                    px={1}
                    py={1.5}
                    border="none"
                    shadow="none"
                  >
                    {rowProgramCollection.items.map((item) => (
                      <Listbox.Item
                        key={item.value || 'none'}
                        item={item}
                        px={2.5}
                        py={1.5}
                        rounded="md"
                        cursor="pointer"
                        minH="auto"
                      >
                        <Text
                          fontSize="sm"
                          color={item.value ? 'fg' : 'fg.muted'}
                          fontWeight={item.value === programId ? 'medium' : 'normal'}
                          truncate
                        >
                          {item.label}
                        </Text>
                        <Listbox.ItemIndicator />
                      </Listbox.Item>
                    ))}
                  </Listbox.Content>
                </Listbox.Root>
              </Popover.Content>
            </Popover.Positioner>
          </Popover.Root>
        )
      ) : (
        <HStack
          px={3}
          py={isLocked ? 2 : 1.5}
          rounded="lg"
          border="1px solid"
          borderColor="border"
          bg="bg"
          gap={3}
        >
          <Text fontSize="sm" color="fg.muted" flexShrink={0}>
            {t('bulkEmail.compose.program.label')}:
          </Text>
          {isLocked ? (
            <HStack flex={1} justify="space-between">
              <Text fontSize="sm">{programName ?? programId}</Text>
              <Icon color="fg.muted" asChild boxSize={4.5}>
                <Lock />
              </Icon>
            </HStack>
          ) : (
            <SelectRoot
              collection={programCollection}
              value={programId ? [programId] : []}
              onValueChange={(e) => handleProgramChange(e.value[0] ?? '')}
              size="sm"
              flex={1}
            >
              <SelectTrigger
                border="none"
                bg="transparent"
                minH="unset"
                h="auto"
                px={0}
                _focusVisible={{ boxShadow: 'none', outline: 'none' }}
              >
                <SelectValueText placeholder={t('bulkEmail.compose.program.placeholder')} />
              </SelectTrigger>
              <SelectContent>
                {programCollection.items.map((item) => (
                  <SelectItem key={item.value} item={item}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </SelectRoot>
          )}
        </HStack>
      )}

      {incompatible.length > 0 && (
        <HStack
          mt={1.5}
          px={3}
          py={2}
          rounded="lg"
          bg="orange.50"
          border="1px solid"
          borderColor="orange.200"
          gap={2}
        >
          <Icon color="orange.600" asChild boxSize={4} flexShrink={0}>
            <WarningTriangle />
          </Icon>
          <Text fontSize="xs" color="orange.800" flex={1}>
            {t('bulkEmail.compose.program.incompatibleWarning', { count: incompatible.length })}
          </Text>
          <Button
            size="xs"
            variant="ghost"
            colorPalette="orange"
            onClick={handleRemoveIncompatible}
            flexShrink={0}
          >
            {t('bulkEmail.compose.program.removeIncompatible')}
          </Button>
        </HStack>
      )}
    </>
  );
}

export default EmailProgramSelector;
